import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import { Download as DownloadIcon } from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
  InputAdornment,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  Event as EventIcon,
  Today as TodayIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Block as BlockIcon,
  Person as PersonIcon,
  Badge as BadgeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  fetchMyLeaves,
  updateLeaveAction,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsActions";
import {
  selectLeaves,
  selectLeaveLoading,
  selectUpdatingAction,
} from "store/features/company/leaveManagement/leaveDetails/leaveDetailsSelectors";
import leavesSvg from "assets/folders/leaves.svg";
import usePermissions from "hooks/usePermissions";
import { fetchLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeActions";
import { selectLeaveTypes } from "store/features/company/leaveManagement/leaveType/leaveTypeSelectors";

const LeaveDetails = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const leaves = useSelector(selectLeaves);
  const loading = useSelector(selectLeaveLoading);
  const updatingAction = useSelector(selectUpdatingAction);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [leaveToApprove, setLeaveToApprove] = useState(null);
  const [approving, setApproving] = useState(false);

  //colour that i added extra from different api
  const leaveTypes = useSelector(selectLeaveTypes);
  useEffect(() => {
    dispatch(fetchMyLeaves({ limit: "all" }));
    dispatch(fetchLeaveTypes());
  }, [dispatch]);
  const getLeaveTypeColor = (leaveTypeId) => {
    const leaveType = leaveTypes.find((lt) => lt.id === leaveTypeId);
    return leaveType?.color || "#f59e0b";
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  //  const [dateFilter, setDateFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const parseDateStr = (dateVal) => {
    if (!dateVal) return "";
    if (
      typeof dateVal === "string" &&
      dateVal.length >= 10 &&
      dateVal.includes("-")
    ) {
      return dateVal.split("T")[0];
    }
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { hasPermission, hasAnyPermission } = usePermissions();

  const filteredLeaves = leaves.filter((leave) => {
    const matchesSearch =
      searchTerm === "" ||
      leave.leave_type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${leave.first_name} ${leave.last_name}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      leave.employee_code?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || leave.status === statusFilter;

    const matchesDate = (() => {
      if (!fromDate && !toDate) return true;
      const startDate = parseDateStr(leave.start_date);
      const endDate = parseDateStr(leave.end_date);
      const matchesFrom = !fromDate || (endDate && endDate >= fromDate);
      const matchesTo = !toDate || (startDate && startDate <= toDate);
      return matchesFrom && matchesTo;
    })();

    return matchesSearch && matchesStatus && matchesDate;
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      pending: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
      },
      rejected: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
      },
      cancelled: {
        bg: "#f3f4f6",
        text: "#4b5563",
        darkBg: "rgba(107,114,128,0.25)",
        darkText: "#9ca3af",
      },
      applied: {
        bg: "#e0e7ff",
        text: "#3730a3",
        darkBg: "rgba(99,102,241,0.25)",
        darkText: "#a5b4fc",
      },
    };
    return colors[status] || colors.pending;
  };

  const StatusBadge = ({ status }) => {
    const colors = getStatusColor(status);

    const getIcon = () => {
      switch (status) {
        case "approved":
        case "applied":
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
        case "pending":
          return <PendingIcon sx={{ fontSize: 14 }} />;
        case "rejected":
          return <CancelIcon sx={{ fontSize: 14 }} />;
        case "cancelled":
          return <BlockIcon sx={{ fontSize: 14 }} />;
        default:
          return <PendingIcon sx={{ fontSize: 14 }} />;
      }
    };

    const getLabel = () => {
      if (status === "applied") return "Auto Deducted";
      return status.charAt(0).toUpperCase() + status.slice(1);
    };

    return (
      <Chip
        icon={getIcon()}
        label={getLabel()}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          fontWeight: 500,
          fontSize: "0.75rem",
          height: "24px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };
  const formatDays = (days) => {
    const num = parseFloat(days);
    if (isNaN(num)) return "0 days";

    const wholeDays = Math.floor(num);
    const fraction = num - wholeDays;

    if (fraction === 0) {
      return `${wholeDays} ${wholeDays === 1 ? "day" : "days"}`;
    }

    if (fraction === 0.5) {
      if (wholeDays === 0) return "½ day";
      return `${wholeDays} ½ days`;
    }

    // For other fractions like 0.25, 0.75, etc.
    if (fraction === 0.25) {
      if (wholeDays === 0) return "¼ day";
      return `${wholeDays} ¼ days`;
    }

    if (fraction === 0.75) {
      if (wholeDays === 0) return "¾ day";
      return `${wholeDays} ¾ days`;
    }

    return `${num} days`;
  };
  const HalfDayBadge = ({ isHalfDay, session }) => {
    if (!isHalfDay) return null;

    return (
      <Chip
        label={session === "first_half" ? "First Half" : "Second Half"}
        size="small"
        sx={{
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          fontWeight: 500,
          fontSize: "0.7rem",
          height: "20px",
          ...(theme.palette.mode === "dark" && {
            backgroundColor: "rgba(59,130,246,0.25)",
            color: "#93c5fd",
          }),
        }}
      />
    );
  };
  const handleApproveClick = (leave) => {
    setLeaveToApprove(leave);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (leaveToApprove) {
      setApproving(true);
      const result = await dispatch(
        updateLeaveAction(leaveToApprove.id, { action: "approved" }),
      );
      if (result.success) {
        setApproveDialogOpen(false);
        setLeaveToApprove(null);
      }
      setApproving(false);
    }
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setLeaveToApprove(null);
  };

  const handleRejectClick = (leave) => {
    setSelectedLeave(leave);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (selectedLeave && rejectionReason.trim()) {
      const result = await dispatch(
        updateLeaveAction(selectedLeave.id, {
          action: "rejected",
          rejection_reason: rejectionReason,
        }),
      );
      if (result.success) {
        setRejectDialogOpen(false);
        setSelectedLeave(null);
        setRejectionReason("");
      }
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setSelectedLeave(null);
    setRejectionReason("");
  };

  if (!hasPermission("leave_details.view")) {
    return null;
  }
  const exportLeaveDetailsToExcel = () => {
    if (!filteredLeaves || filteredLeaves.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Yellow header style
    const yellowHeaderStyle = {
      font: {
        bold: true,
        sz: 12,
        color: { rgb: "000000" },
      },
      fill: {
        fgColor: { rgb: "FFFF00" },
      },
      alignment: {
        horizontal: "center",
        vertical: "center",
      },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["LEAVE DETAILS REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 8 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // Summary
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Leaves", filteredLeaves.length],
        [
          "Pending",
          filteredLeaves.filter((l) => l.status === "pending").length,
        ],
        [
          "Approved",
          filteredLeaves.filter((l) => l.status === "approved").length,
        ],
        [
          "Rejected",
          filteredLeaves.filter((l) => l.status === "rejected").length,
        ],
        [
          "Cancelled",
          filteredLeaves.filter((l) => l.status === "cancelled").length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 8;

    // Headers
    const headers = [
      "ID",
      "Employee ID",
      "Employee Name",
      "Employee Code",
      "Leave Type",
      "Start Date",
      "End Date",
      "Days",
      "Is Half Day",
      "Half Day Session",
      "Reason",
      "Status",
      "Applied On",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    // Apply yellow header style to header row
    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // Data
    filteredLeaves.forEach((leave) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            leave.id || "",
            leave.employee_id || "",
            `${leave.first_name || ""} ${leave.last_name || ""}`.trim(),
            leave.employee_code || "",
            leave.leave_type_name || "",
            leave.start_date ? formatDate(leave.start_date) : "",
            leave.end_date ? formatDate(leave.end_date) : "",
            leave.days || "",
            leave.is_half_day === 1 ? "Yes" : "No",
            leave.half_day_session || "",
            leave.reason || "",
            leave.status || "",
            leave.created_at ? formatDateTime(leave.created_at) : "",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    // Column widths
    ws["!cols"] = headers.map((_, index) => {
      const widths = {
        0: 15, // ID
        1: 18, // Employee ID
        2: 25, // Employee Name
        3: 20, // Employee Code
        4: 22, // Leave Type
        5: 20, // Start Date
        6: 20, // End Date
        7: 15, // Days
        8: 18, // Is Half Day
        9: 22, // Half Day Session
        10: 35, // Reason
        11: 18, // Status
        12: 25, // Applied On
      };
      return { wch: widths[index] || 20 };
    });

    XLSX.utils.book_append_sheet(wb, ws, "Leave Details");

    // Write file
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, `Leave_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={leavesSvg}
              alt="Leaves"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                Leave Details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                }}
              >
                View and manage all leave requests from employees
              </Typography>
            </Box>
          </Stack>

          {/* Export Excel Button */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportLeaveDetailsToExcel}
              sx={{
                background: "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow: "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Excel
            </Button>
          </Box>
        </Stack>
      </Box>

      {/* Search and Filter Section */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems={{ lg: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          sx={{ flex: 1 }}
        >
          <TextField
            placeholder="Search by employee name, code, leave type or reason..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            variant="outlined"
            size="small"
            sx={{
              flex: 1,
              minWidth: { sm: 260 },
              maxWidth: { lg: 380 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "background.paper",
                "& fieldset": {
                  borderColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
                "&:hover fieldset": {
                  borderColor: "#f59e0b",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#f59e0b",
                  borderWidth: "2px",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "4px",
              borderRadius: "12px",
              bgcolor: "background.paper",
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
              }`,
            }}
          >
            <DatePicker
              label="From"
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(v) => {
                setFromDate(
                  v && dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : "",
                );
                setPage(0);
              }}
              maxDate={toDate ? dayjs(toDate) : undefined}
              slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
            />
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              to
            </Typography>
            <DatePicker
              label="To"
              value={toDate ? dayjs(toDate) : null}
              onChange={(v) => {
                setToDate(
                  v && dayjs(v).isValid() ? dayjs(v).format("YYYY-MM-DD") : "",
                );
                setPage(0);
              }}
              minDate={fromDate ? dayjs(fromDate) : undefined}
              slotProps={{ textField: { size: "small", sx: { width: 150 } } }}
            />
            <Button
              size="small"
              variant="outlined"
              disabled={!fromDate && !toDate}
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(0);
              }}
              sx={{
                height: "36px",
                borderRadius: "8px",
                fontSize: "0.8125rem",
                textTransform: "none",
              }}
            >
              Clear
            </Button>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          {["all", "pending", "approved", "rejected", "cancelled"].map(
            (filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? "contained" : "outlined"}
                onClick={() => {
                  setStatusFilter(filter);
                  setPage(0);
                }}
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "8px",
                  px: 2,
                  py: 0.7,
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  ...(statusFilter === filter && {
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    boxShadow: "0 4px 6px -1px rgba(245, 158, 11, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    },
                  }),
                  ...(statusFilter !== filter && {
                    borderColor:
                      theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
                    color:
                      theme.palette.mode === "dark" ? "#d1d5db" : "#6b7280",
                    "&:hover": {
                      borderColor: "#f59e0b",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(245,158,11,0.1)"
                          : "#fffbeb",
                    },
                  }),
                }}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)}
              </Button>
            ),
          )}
        </Stack>
      </Stack>

      {/* Loading State */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress
              size={48}
              sx={{
                color: "primary.main",
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading leave requests...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                : "0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.02)",
            border: `1px solid ${
              theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
            }`,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 1000 }}>
              <TableHead
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    ID / Employee ID
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Employee
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Leave Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Duration
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Days
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Reason
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}
                  >
                    Applied On
                  </TableCell>
                  {hasAnyPermission([
                    "leave_details.approve",
                    "leave_details.reject",
                  ]) && (
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "text.secondary",
                      }}
                    >
                      Actions
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                      <EventIcon
                        sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }}
                      />
                      <Typography
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        No leave requests found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaves
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((leave, index) => (
                      <TableRow
                        key={leave.unique_id || leave.id}
                        component={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        sx={{
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30,41,59,0.5)"
                                : "#f9fafb",
                          },
                        }}
                      >
                        {/* ID and Employee ID */}
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 700,
                                fontSize: "0.875rem",
                                color: "primary.main",
                              }}
                            >
                              #{leave.id}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.7rem",
                                color: "text.secondary",
                              }}
                            >
                              Emp ID: {leave.employee_id}
                            </Typography>
                          </Stack>
                        </TableCell>
                        {/* Employee Name & Code Column */}
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: "0.875rem",
                                  color: "text.primary",
                                }}
                              >
                                {leave.first_name} {leave.last_name}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <BadgeIcon
                                  sx={{ fontSize: 12, color: "#9ca3af" }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.7rem", color: "#9ca3af" }}
                                >
                                  {leave.employee_code}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>
                        {/* Leave Type Column */}
                        <TableCell>
                          {/* <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          > */}
                          {/* <Stack alignItems="center" spacing={0.5}>
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: "8px",
                                  backgroundColor: getLeaveTypeColor(
                                    leave.leave_type_id,
                                  ),
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <EventIcon
                                  sx={{ fontSize: 16, color: "#ffffff" }}
                                />
                              </Box>
                              <Chip
                                label={`ID: ${leave.leave_type_id}`}
                                size="small"
                                sx={{
                                  height: "18px",
                                  fontSize: "0.6rem",
                                  fontFamily: "monospace",
                                  fontWeight: 500,
                                  backgroundColor: getLeaveTypeColor(
                                    leave.leave_type_id,
                                  ),
                                  color: "#ffffff",
                                  "& .MuiChip-label": {
                                    px: 0.8,
                                    py: 0,
                                  },
                                }}
                              />
                            </Stack> */}
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.875rem",
                              color: "text.primary",
                            }}
                          >
                            {leave.leave_type_name}
                          </Typography>
                          {/* </Stack> */}
                        </TableCell>

                        {/* Duration Column */}
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              <b>From: </b> {formatDate(leave.start_date)}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              <b>To: </b> {formatDate(leave.end_date)}
                            </Typography>
                            {leave.is_half_day === 1 && (
                              <HalfDayBadge
                                isHalfDay={leave.is_half_day === 1}
                                session={leave.half_day_session}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        {/* Days Column */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {parseFloat(leave.days).toString()}{" "}
                          </Typography>
                        </TableCell>

                        {/* Reason Column */}
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#d1d5db"
                                  : "#4b5563",
                              fontStyle: "italic",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={leave.reason}
                          >
                            <span style={{ color: "#3b82f6", fontWeight: 500 }}>
                              "
                            </span>
                            {leave.reason || "-"}
                            <span style={{ color: "#3b82f6", fontWeight: 500 }}>
                              "
                            </span>
                          </Typography>
                        </TableCell>

                        {/* Status Column */}
                        <TableCell>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <StatusBadge status={leave.status} />

                            {leave.status === "approved" &&
                              leave.approved_by_name && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="baseline"
                                  sx={{ pl: 0.5, maxWidth: 180 }}
                                  title={`Approved by ${leave.approved_by_name}`}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 400,
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#64748b"
                                          : "#94a3b8",
                                      whiteSpace: "nowrap",
                                      flexShrink: 0,
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    Approved by
                                  </Typography>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "0.7rem",
                                      fontWeight: 600,
                                      color:
                                        theme.palette.mode === "dark"
                                          ? "#cbd5e1"
                                          : "#334155",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {leave.approved_by_name}
                                  </Typography>
                                </Stack>
                              )}
                          </Stack>
                        </TableCell>

                        {/* Applied On Column */}
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <EventIcon
                              sx={{ fontSize: 14, color: "primary.main" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.875rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4b5563",
                              }}
                            >
                              {formatDateTime(leave.created_at)}
                            </Typography>
                          </Stack>
                        </TableCell>

                        {/* Actions Column */}
                        <TableCell>
                          {leave.status === "pending" && (
                            <Stack direction="row" spacing={1}>
                              {hasPermission("leave_details.approve") && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleApproveClick(leave)}
                                  disabled={updatingAction}
                                  startIcon={
                                    <ThumbUpIcon sx={{ fontSize: 16 }} />
                                  }
                                  sx={{
                                    backgroundColor: "#10b981",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    "&:hover": {
                                      backgroundColor: "#059669",
                                    },
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                              {hasPermission("leave_details.reject") && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => handleRejectClick(leave)}
                                  disabled={updatingAction}
                                  startIcon={
                                    <ThumbDownIcon sx={{ fontSize: 16 }} />
                                  }
                                  sx={{
                                    backgroundColor: "#ef4444",
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    fontWeight: 500,
                                    borderRadius: "8px",
                                    "&:hover": {
                                      backgroundColor: "#dc2626",
                                    },
                                  }}
                                >
                                  Reject
                                </Button>
                              )}
                            </Stack>
                          )}
                          {leave.status !== "pending" && (
                            <Typography
                              sx={{
                                fontSize: "0.75rem",
                                color:
                                  leave.status === "approved"
                                    ? "#22c55e"
                                    : theme.palette.mode === "dark"
                                    ? "#6b7280"
                                    : "#9ca3af",
                                fontStyle: "italic",
                              }}
                            >
                              {leave.status === "approved" && "Approved"}
                              {leave.status === "rejected" && "Rejected"}
                              {leave.status === "cancelled" && "Cancelled"}
                              {(leave.status === "applied" || leave.is_auto_deduction === 1) && "Auto Deducted"}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pagination */}
      {!loading && filteredLeaves.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredLeaves.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            mt: 2,
            color: "text.secondary",
            ".MuiTablePagination-selectIcon, .MuiTablePagination-actions button":
              {
                color: "text.secondary",
              },
          }}
        />
      )}

      {/* Empty State for No Search Results */}
      {!loading && filteredLeaves.length === 0 && leaves.length > 0 && (
        <Paper
          sx={{
            mt: 4,
            p: 4,
            textAlign: "center",
            borderRadius: "16px",
            bgcolor: "background.paper",
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
          <Typography
            sx={{
              color: "text.secondary",
              mb: 2,
            }}
          >
            No leave requests match your search criteria
          </Typography>
          <Button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setPage(0);
            }}
            sx={{
              color: "primary.main",
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            }}
          >
            Clear filters
          </Button>
        </Paper>
      )}

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Red Banner for Reject */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            <IconButton
              onClick={handleRejectCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            <Box
              sx={{
                width: 80,
                height: 80,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
              }}
            >
              <ThumbDownIcon sx={{ fontSize: 40, color: "#ef4444" }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Reject Leave Request
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to reject this leave request from
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "#ef4444",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{selectedLeave?.first_name} {selectedLeave?.last_name}"
            </Typography>

            <TextField
              autoFocus
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              label="Rejection Reason"
              placeholder="Please provide a reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: "background.default",
                },
              }}
            />
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleRejectCancel}
              disabled={updatingAction}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              disabled={!rejectionReason.trim() || updatingAction}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  boxShadow: "0 6px 16px rgba(239, 68, 68, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {updatingAction ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Confirm Rejection"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
      {/* Approve Confirmation Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        maxWidth="xs"
        fullWidth
        TransitionComponent={motion.div}
        TransitionProps={{
          timeout: 300,
          style: { overflow: "visible" },
        }}
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "20px",
            overflow: "hidden",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
            maxWidth: "400px",
            margin: "16px",
            boxShadow: theme.shadows[24],
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
        >
          {/* Green Banner for Approve */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              height: "6px",
            }}
          />

          <Box sx={{ p: 3, position: "relative" }}>
            {/* Close Icon Button */}
            <IconButton
              onClick={handleApproveCancel}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>

            {/* SVG Icon - You can use a checkmark or approval icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                margin: "0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
                backgroundColor: "#d1fae5",
                borderRadius: "50%",
              }}
            >
              <ThumbUpIcon sx={{ fontSize: 40, color: "#10b981" }} />
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "text.primary",
                textAlign: "center",
                mb: 2,
              }}
            >
              Approve Leave Request
            </Typography>

            <Typography
              sx={{
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                fontSize: "0.95rem",
                lineHeight: 1.5,
                textAlign: "center",
                mb: 1,
              }}
            >
              Are you sure you want to approve this leave request from
            </Typography>

            <Typography
              sx={{
                fontWeight: 600,
                color: "#10b981",
                fontSize: "1rem",
                textAlign: "center",
                mb: 2,
                wordBreak: "break-word",
              }}
            >
              "{leaveToApprove?.first_name} {leaveToApprove?.last_name}"
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.85rem",
                lineHeight: 1.4,
                textAlign: "center",
              }}
            >
              <b>Type: </b> {leaveToApprove?.leave_type_name}
              <br />
              <b>Duration: </b>
              {leaveToApprove?.days} days
            </Typography>
          </Box>

          <Box sx={{ p: 3, pt: 0, display: "flex", gap: 2 }}>
            <Button
              onClick={handleApproveCancel}
              disabled={approving}
              fullWidth
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                color: theme.palette.mode === "dark" ? "#d1d5db" : "#4b5563",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6",
                border: `1px solid ${
                  theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
                }`,
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveConfirm}
              disabled={approving}
              fullWidth
              variant="contained"
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1,
                fontSize: "0.875rem",
                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  boxShadow: "0 6px 16px rgba(16, 185, 129, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  background:
                    "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                  opacity: 0.6,
                },
              }}
            >
              {approving ? (
                <CircularProgress size={20} sx={{ color: "#ffffff" }} />
              ) : (
                "Approve"
              )}
            </Button>
          </Box>
        </motion.div>
      </Dialog>
    </Box>
  );
};

export default LeaveDetails;
